import React, { useCallback } from 'react';

import { NamedLink } from '../../components';
import { track } from '../../util/analytics';
import { adClickIds } from '../../util/attribution';

// Shared params for the "create a new listing" flow used by every space-owner CTA.
export const LIST_SPACE_PARAMS = {
  slug: 'draft',
  id: '00000000-0000-0000-0000-000000000000',
  type: 'new',
  tab: 'details',
};

/**
 * Primary "list your space" call-to-action for the Space Owner landing page.
 *
 * Renders a NamedLink into the new-listing flow. The importable `generate_lead`
 * conversion is intentionally NOT fired here on click — it now fires downstream
 * when the new-listing wizard actually begins (the `listing_start` moment in
 * `EditListingPage`), so the DemandGen-B2B campaign optimizes toward real intent
 * rather than a button click. Ad attribution (gclid) survives the deferral via
 * the 90-day cookie in `util/attribution.js`.
 *
 * The generic `cta_click` event is emitted by the global click listener
 * (`util/clickTracking.js`). That listener only fires for outbound links and
 * elements opted in via `[data-track-cta]`, so the attribute is set explicitly
 * here — every CTA on the page stays individually measurable in GA4 without
 * bespoke handlers.
 *
 * What this component DOES fire is `list_space_intent` — the supply-side intent
 * signal. Measured on production 26.08.2026: clicking one of these CTAs produced
 * nothing but a `PageVisit`, because `generate_lead` only fires once the wizard
 * begins and `cta_click` is deliberately not mirrored to Reddit. Between this
 * click and a completed registration the funnel was therefore invisible — which
 * is precisely where it breaks, at the sign-up wall. `list_space_intent` closes
 * that blind spot without touching what `generate_lead` means.
 *
 * Navigation here is client-side (react-router), so there is no unload that could
 * cut the analytics beacon short.
 *
 * @component
 * @param {Object} props
 * @param {string} props.ctaId - Stable DOM id; used as `click_id`/`cta_id` in analytics
 * @param {string?} props.className
 * @param {any} props.children - CTA label
 * @returns {JSX.Element}
 */
const ListSpaceCta = props => {
  const { ctaId, className, children } = props;

  const handleClick = useCallback(() => {
    track.listSpaceIntent({
      cta_id: ctaId,
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      ...adClickIds(),
    });
  }, [ctaId]);

  return (
    <NamedLink
      id={ctaId}
      name="EditListingPage"
      params={LIST_SPACE_PARAMS}
      className={className}
      onClick={handleClick}
      dataTrackCta={ctaId}
    >
      {children}
    </NamedLink>
  );
};

export default ListSpaceCta;
